/**
 * The LISTS filter group — a collapsible "LISTS" section holding one "Lists"
 * multi-select chip ("All lists" when nothing is selected) that scopes a
 * candidate pool by watchlist membership.
 *
 * SE-8627: extracted from the pipeline's `AddPlayersFilterPanelView` (where
 * SE-7094 introduced it) so the Watchlist `FilterSheet` — which the scenario
 * pitch's "Add players to" panel mounts — renders the SAME control rather
 * than a copy that could drift (the SE-8608 lesson for the other sections).
 *
 * Its own group, not a BASIC control: it filters pool membership, not player
 * attributes. Absent when there are no lists — an empty multi-select would be
 * a control that can do nothing.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/scouting/components/molecules/ListsFilterSection/ListsFilterSectionView
 */

import React, { memo } from "react";

import type { FilterOption } from "@/features/scouting/components/molecules/FilterChip";
import FilterChip from "@/features/scouting/components/molecules/FilterChip";
import SectionHeader from "@/features/scouting/components/molecules/SectionHeader";

import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for {@link ListsFilterSectionView}. */
export interface ListsFilterSectionProps {
  /** The lists on offer, as {id, label}. */
  readonly options: readonly FilterOption[];
  /** Selected list ids (the host's draft). */
  readonly selectedIds: readonly string[];
  /** Toggle one list. */
  readonly onToggle: (id: string) => void;
  /** Clear the selection back to All lists. */
  readonly onClear: () => void;
  /** Whether the section is expanded. */
  readonly isExpanded: boolean;
  /** Expand / collapse the section. */
  readonly onToggleSection: () => void;
  /** Whether the chip's dropdown is open. */
  readonly isChipOpen: boolean;
  /** Open / close the chip's dropdown. */
  readonly onToggleChip: () => void;
  /** Close whichever chip dropdown is open. */
  readonly onCloseChip: () => void;
  /** Whether the options are still loading. */
  readonly loading: boolean;
}

/**
 * Render the LISTS group.
 * @param root0 - Component props; see {@link ListsFilterSectionProps}.
 * @param root0.options - The lists on offer.
 * @param root0.selectedIds - Selected list ids.
 * @param root0.onToggle - Toggle one list.
 * @param root0.onClear - Clear the selection.
 * @param root0.isExpanded - Whether the section is expanded.
 * @param root0.onToggleSection - Expand / collapse the section.
 * @param root0.isChipOpen - Whether the chip dropdown is open.
 * @param root0.onToggleChip - Open / close the chip dropdown.
 * @param root0.onCloseChip - Close the open chip dropdown.
 * @param root0.loading - Whether the options are still loading.
 * @returns The section, or null when there are no lists.
 */
const ListsFilterSectionView = ({
  options,
  selectedIds,
  onToggle,
  onClear,
  isExpanded,
  onToggleSection,
  isChipOpen,
  onToggleChip,
  onCloseChip,
  loading,
}: ListsFilterSectionProps) =>
  options.length > 0 ? (
    <Stack className="border-b border-outline-default">
      <SectionHeader
        title="LISTS"
        isExpanded={isExpanded}
        onToggle={onToggleSection}
        activeCount={selectedIds.length}
      />
      {isExpanded ? (
        <Stack space="4" className="overflow-visible px-6 pb-4">
          {/* Raised while open — the same stacking fix every chip row in the
              filter sections carries. */}
          <Stack space="1" className={isChipOpen ? "z-50" : "z-0"}>
            <Text
              variant="unstyled"
              className="text-title-sm text-content-muted"
            >
              Lists
            </Text>
            <FilterChip
              label="List"
              options={options}
              selectedIds={selectedIds}
              onToggleOption={onToggle}
              onClearAll={onClear}
              isOpen={isChipOpen}
              onToggle={onToggleChip}
              onClose={onCloseChip}
              loading={loading}
            />
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  ) : null;

ListsFilterSectionView.displayName = "ListsFilterSectionView";

export default memo(ListsFilterSectionView);
